/**
 * Pitch and curve math. Pure — no AudioContext, no DOM, no state.
 *
 * Transferred from the local Chirp sources (see docs/CHIRP-TRANSFER.md):
 *   Chirp/bittern.html — GULAR: midiHz, degreeSemis, cutoffHz, unisonFan, driveCurve
 *   Chirp/trill.html   — SYRINX: the identical midiHz / unisonFan pair
 * One frequency formula for the whole instrument; every engine calls this one.
 */

import {
  clamp as coreClamp,
  cutoffHz as coreCutoffHz,
  degreeSemis as coreDegreeSemis,
  driveCurve as coreDriveCurve,
  midiHz as coreMidiHz,
  unisonFan as coreUnisonFan,
} from '../vendor/av-core.mjs';

// SINCE AV-CORE PHASE 3 (flyway intent 2026-10-04-av-core-inline): clamp,
// midiHz, degreeSemis, cutoffHz, unisonFan and driveCurve ARE Chirp's
// single-source av-core pitch module (../vendor/av-core.mjs), re-exported with
// gesture_synth's types. Same names, same signatures, same behaviour; edit them
// in Chirp av-core/pitch.js, not here. lerp and degreeFromHeight have no
// av-core counterpart and stay local.

export const clamp: (lo: number, hi: number, v: number) => number = coreClamp;

/** Linear interpolate. (Local — not in av-core.) */
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Equal temperament with a cents trim. The one frequency formula. (av-core) */
export const midiHz: (midi: number, cents?: number) => number = coreMidiHz;

/**
 * Any integer degree (negatives fine) → semitone offset from the scale root,
 * octave-wrapped. The rail: whatever height a hand is dragged to, the pitch
 * lands in-scale. (av-core)
 */
export const degreeSemis: (steps: readonly number[], deg: number) => number = coreDegreeSemis;

/**
 * The cutoff law: 0..1 → lo..hi Hz, logarithmic, clamped. Defaults are the
 * vocabulary doc's range (180 Hz → 9 kHz); config supplies the endpoints.
 * (av-core — the flock's shared law; Chirp's 40..8000 is passed as parameters)
 */
export const cutoffHz: (v: number, lo?: number, hi?: number) => number = coreCutoffHz;

/** Unison fan: voice i of U spread across -1..1, symmetric, 0 when alone. (av-core) */
export const unisonFan: (i: number, u: number) => number = coreUnisonFan;

/** The screamer's transfer curve — odd, bounded, identity at zero drive. (av-core) */
export const driveCurve: (amount: number, n?: number) => Float32Array = coreDriveCurve;

/**
 * Degree selection with hysteresis at each boundary, in degree-steps.
 * Carried over from spike/index.html `degreeFrom` — a bare floor() machine-guns
 * whenever a hand rests on a boundary.
 */
export function degreeFromHeight(height01: number, cur: number, n: number, hyst = 0.25): number {
  const pos = clamp(0, n - 1e-6, height01 * n);
  const cand = Math.floor(pos);
  if (cand === cur) return cur;
  return (cand > cur ? pos > cur + 1 + hyst : pos < cur - hyst) ? cand : cur;
}
